import React, { useEffect, useState, useCallback } from "react";
import { apiFetch } from "../api";
import { formatDate } from "../utils/dateUtils";
import { useLoading } from "../contexts/LoadingContext";
import GenderBadge from "./GenderBadge";
import {
  statusStyleMap,
  clientStatusOptions,
} from "../utils/statusColors";

// Theme-aware button class presets so colours follow the active theme
// (purple / teal / pink) instead of hardcoded blue/red.
const BTN_PRIMARY =
  "rounded-lg bg-[var(--color-primary)] text-[var(--color-on-primary)] hover:bg-[var(--color-primary-hover)] disabled:opacity-50 transition-colors";
const BTN_PRIMARY_SM =
  "rounded text-xs bg-[var(--color-primary)] text-[var(--color-on-primary)] hover:bg-[var(--color-primary-hover)] disabled:opacity-50 transition-colors";
const BTN_SECONDARY =
  "rounded-lg border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] disabled:opacity-50 transition-colors";
const BTN_SECONDARY_SM =
  "rounded text-xs border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] disabled:opacity-50 transition-colors";
const BTN_DANGER_SM =
  "rounded text-xs bg-[var(--color-danger-text)] text-white hover:opacity-90 disabled:opacity-50 transition-opacity";

const statusOptions = [
  { value: "viewed", label: "Viewed" },
  { value: "interested", label: "Interested" },
  { value: "contacted", label: "Contacted" },
  { value: "accepted", label: "Accepted" },
  { value: "rejected", label: "Rejected" },
];

// Shared demographic-facts card. Renders the same visual layout (ID
// header, name + gender badge, bullet-list sections for personal /
// caste / house / profession / income / qualifications / family) for
// both the matched profile AND the client — they're both PublicUsers
// post-merge, so they share the same field shape.
//
// Returns the inner contents WITHOUT a wrapping <section>, so callers
// can append extra sections (e.g. client-side match preferences) inside
// the same card.
const ProfileFactsCard = ({ u }) => {
  if (!u) return null;
  const heightStr =
    u.heightFeet || u.heightInches
      ? `${u.heightFeet ?? "—"}ft ${u.heightInches ?? 0}in`
      : u.height || "—";
  const houseLine =
    [
      u.houseSizeMarla ? `${u.houseSizeMarla} Marla` : null,
      u.houseLocation,
      u.area,
      u.city,
      u.country,
    ]
      .filter(Boolean)
      .join(", ") + (u.houseStatus ? ` (${u.houseStatus})` : "");
  return (
    <>
      <div className="text-xs text-[var(--color-text-muted)]">
        ID {u.referenceId || "—"}
      </div>
      <div className="mb-2 flex items-center gap-2 font-semibold text-[var(--color-text-strong)] text-lg">
        <span>{u.fullName || "—"}</span>
        <GenderBadge gender={u.gender} />
      </div>

      <div className="font-medium text-sm">Personal Details</div>
      <div className="flex flex-wrap items-center gap-2 text-sm text-[var(--color-text)] mb-3">
        <span>•</span>
        <span>{u.age ?? "—"}</span>
        <span>•</span>
        <span className="capitalize">{u.gender || "—"}</span>
        <span>•</span>
        <span>{heightStr}</span>
        <span>•</span>
        <span className="capitalize">{u.maritalStatus || "—"}</span>
      </div>

      <div className="mt-2 text-sm text-[var(--color-text)]">
        <div className="font-medium text-sm">Date of Birth</div>
        <ul className="list-disc ml-5 mt-1 text-xs">
          <li>
            <span className="text-xs">
              {u.dob ? formatDate(u.dob) : "Not specified"}
            </span>
          </li>
        </ul>
      </div>

      <div className="mt-2 text-sm text-[var(--color-text)]">
        <div className="font-medium text-sm">Caste</div>
        <ul className="list-disc ml-5 mt-1 text-xs">
          <li>
            <span className="text-xs">
              {u.caste || u.cast || "Not specified"}
            </span>
          </li>
        </ul>
      </div>

      <div className="mt-2 text-sm text-[var(--color-text)]">
        <div className="font-medium text-sm">House</div>
        <ul className="list-disc ml-5 mt-1 text-xs">
          <li>
            <span>{houseLine || "Not specified"}</span>
          </li>
        </ul>
      </div>

      <div className="mt-2 text-sm text-[var(--color-text)]">
        <div className="font-medium text-sm">Profession</div>
        <ul className="list-disc ml-5 mt-1 text-xs">
          <li>
            <span>{u.profession || "Not specified"}</span>
          </li>
        </ul>
      </div>

      <div className="mt-2 text-sm text-[var(--color-text)]">
        <div className="font-medium text-sm">Monthly Income</div>
        <ul className="list-disc ml-5 mt-1 text-xs">
          <li>
            <span>
              {u.monthlyIncome
                ? `Rs${Number(u.monthlyIncome).toLocaleString()}`
                : "Not specified"}
            </span>
          </li>
        </ul>
      </div>

      <div className="mt-2 text-sm text-[var(--color-text)]">
        <div className="font-medium text-sm">Qualification</div>
        <ul className="list-disc ml-5 mt-1 text-xs">
          <li>Education: {u.education || "Not specified"}</li>
          <li>
            Institute of Education:{" "}
            {u.instituteOfQualification || "Not specified"}
          </li>
        </ul>
      </div>

      <div className="font-medium text-sm mt-3">Family Information</div>
      <div className="text-sm text-[var(--color-text)]">
        <div className="font-medium text-[13px]">Parents Details</div>
        <ul className="list-disc ml-5 mt-1 text-xs">
          <li>
            Father: {u.fatherOccupation || "Not specified"}
            {u.fatherDeceased ? " Deceased" : ""}
          </li>
          <li>
            Mother: {u.motherOccupation || "Not specified"}
            {u.motherDeceased ? " Deceased" : ""}
          </li>
        </ul>
      </div>

      {Array.isArray(u.siblings) && u.siblings.length > 0 && (
        <div className="mt-1 text-sm text-[var(--color-text)]">
          <div className="font-medium text-[13px]">Siblings:</div>
          <ul className="list-disc ml-5 mt-1 text-xs">
            {u.siblings.map((s, i) => (
              <li key={i}>
                {s.relation || "—"} — {s.qualification || "—"} /{" "}
                {s.profession || "—"} / {s.maritalStatus || "—"}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-3 text-[12px] text-[var(--color-text)]">
        <div>Contact: {u.contactNumber || "—"}</div>
        {(u.createdAt || u.updatedAt) && (
          <div className="mt-2 text-[10px] text-gray-400">
            {u.createdAt && <>Added: {formatDate(u.createdAt)}&nbsp;</>}
            {u.updatedAt && <>Last Edited: {formatDate(u.updatedAt)}</>}
          </div>
        )}
        {(u.views !== undefined || u.status) && (
          <div className="mt-1 text-[10px] text-gray-400">
            Views: {u.views ?? 0} &nbsp; Status: {u.status || "active"}
          </div>
        )}
      </div>
    </>
  );
};

export const ReferralDetailsModal = ({
  isOpen,
  referral,
  onClose,
  onUpdate,
  isClient = false,
}) => {
  const [status, setStatus] = useState("");
  const [originalStatus, setOriginalStatus] = useState("");
  const [isSavingStatus, setIsSavingStatus] = useState(false);

  const [editComment, setEditComment] = useState("");
  const [editRating, setEditRating] = useState("");

  const [isEditing, setIsEditing] = useState(false);
  const [isSavingComment, setIsSavingComment] = useState(false);

  const [error, setError] = useState(null);

  const [clientDetails, setClientDetails] = useState(null);
  const [profileDetails, setProfileDetails] = useState(null);

  const [loadingDetails, setLoadingDetails] = useState(false);

  // Client-side profile reviews
  const [myReviews, setMyReviews] = useState([]);
  const [reviewText, setReviewText] = useState("");
  const [reviewRating, setReviewRating] = useState("");
  const [isSavingReview, setIsSavingReview] = useState(false);

  // Inline edit state for past comments
  const [editingReviewId, setEditingReviewId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [editingRating, setEditingRating] = useState("");
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const {
    setLoading: setGlobalLoading,
    setGlobalLoadingMessage,
  } = useLoading();

  // =========================
  // FETCH DETAILS
  // =========================
  const fetchClientAndProfileDetails = useCallback(async () => {
    try {
      setLoadingDetails(true);

      // Public users (clients) cannot hit bureau-protected /profiles/:id and
      // /clients/:id endpoints — use the data already populated on the
      // referral by GET /referrals/client.
      if (isClient) {
        setProfileDetails(referral?.profile || null);
        setClientDetails(
          referral?.client
            ? { _id: referral.client._id, user: referral.client }
            : null
        );
        return;
      }

      // Fetch client details — prefer referenceId lookup, fall back to ObjectId
      const clientRefId = referral?.client?.user?.referenceId;
      if (clientRefId) {
        const timestamp = new Date().getTime();
        const clientResponse = await apiFetch(
          `/clients/by-ref/${encodeURIComponent(clientRefId)}?_t=${timestamp}`
        );
        setClientDetails(clientResponse.data);
      } else if (referral?.client?._id) {
        const timestamp = new Date().getTime();
        const clientResponse = await apiFetch(
          `/clients/${referral.client._id}?_t=${timestamp}`
        );
        setClientDetails(clientResponse.data);
      }

      // Fetch profile details — prefer referenceId lookup, fall back to ObjectId
      const profileRefId = referral?.profile?.referenceId;
      if (profileRefId) {
        const timestamp = new Date().getTime();
        const profileResponse = await apiFetch(
          `/profiles/by-ref/${encodeURIComponent(profileRefId)}?_t=${timestamp}`
        );
        setProfileDetails(profileResponse);
      } else if (referral?.profile?._id) {
        const timestamp = new Date().getTime();

        const profileResponse = await apiFetch(
          `/profiles/${referral.profile._id}?_t=${timestamp}`
        );

        setProfileDetails(profileResponse);
      }
    } catch (err) {
      console.error("Failed to fetch details:", err);
      setError("Failed to fetch detailed information");
    } finally {
      setLoadingDetails(false);
    }
  }, [referral, isClient]);

  // =========================
  // CLIENT STATUS UPDATE (inside modal)
  // =========================
  const handleClientStatusChange = async (newStatus) => {
    if (!newStatus) return;
    try {
      setIsSavingStatus(true);
      await apiFetch(`/referrals/client/${referral._id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status: newStatus }),
      });
      setStatus(newStatus);
      setOriginalStatus(newStatus);
      onUpdate?.();
    } catch (err) {
      console.error("Failed to update status:", err);
      setError("Failed to update status");
    } finally {
      setIsSavingStatus(false);
    }
  };

  // =========================
  // PROFILE REVIEWS (client-side)
  // =========================
  const fetchMyReviews = useCallback(async () => {
    if (!isClient || !referral?._id) return;
    try {
      const res = await apiFetch(
        `/referrals/client/${referral._id}/my-profile-reviews`
      );
      setMyReviews(res?.data || []);
    } catch (err) {
      console.error("Failed to load reviews:", err);
    }
  }, [isClient, referral]);

  const handleSaveReview = async () => {
    if (!reviewText.trim()) return;
    try {
      setIsSavingReview(true);
      const res = await apiFetch(
        `/referrals/client/${referral._id}/profile-review`,
        {
          method: "POST",
          body: JSON.stringify({
            comment: reviewText.trim(),
            rating: reviewRating || undefined,
          }),
        }
      );
      if (res?.data) {
        setMyReviews((prev) => [...prev, res.data]);
      }
      setReviewText("");
      setReviewRating("");
    } catch (err) {
      console.error("Failed to save review:", err);
      setError("Failed to save comment");
    } finally {
      setIsSavingReview(false);
    }
  };

  const handleStartEditReview = (review) => {
    setEditingReviewId(review._id);
    setEditingText(review.comment || "");
    setEditingRating(review.rating ? String(review.rating) : "");
  };

  const handleCancelEditReview = () => {
    setEditingReviewId(null);
    setEditingText("");
    setEditingRating("");
  };

  const handleSaveEditReview = async () => {
    if (!editingReviewId || !editingText.trim()) return;
    try {
      setIsSavingEdit(true);
      const res = await apiFetch(
        `/referrals/client/${referral._id}/profile-review/${editingReviewId}`,
        {
          method: "PUT",
          body: JSON.stringify({
            comment: editingText.trim(),
            rating: editingRating === "" ? null : editingRating,
          }),
        }
      );
      if (res?.data) {
        setMyReviews((prev) =>
          prev.map((r) => (r._id === editingReviewId ? res.data : r))
        );
      }
      handleCancelEditReview();
    } catch (err) {
      console.error("Failed to update review:", err);
      setError("Failed to update comment");
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    try {
      await apiFetch(
        `/referrals/client/${referral._id}/profile-review/${reviewId}`,
        { method: "DELETE" }
      );
      setMyReviews((prev) => prev.filter((r) => r._id !== reviewId));
    } catch (err) {
      console.error("Failed to delete review:", err);
      setError("Failed to delete comment");
    }
  };

  // =========================
  // INITIALIZE MODAL — only when opened with a *different* referral, so we
  // don't wipe the user's draft comment when the parent re-syncs the prop
  // after a status update.
  // =========================
  const referralId = referral?._id;
  useEffect(() => {
    if (isOpen && referral) {
      const currentStatus = referral.status || "";

      setStatus(currentStatus);
      setOriginalStatus(currentStatus);

      setError(null);
      setClientDetails(null);
      setProfileDetails(null);
      setMyReviews([]);
      setReviewText("");
      setReviewRating("");
      setEditingReviewId(null);
      setEditingText("");
      setEditingRating("");

      fetchClientAndProfileDetails();
      fetchMyReviews();
    }
    // fetchClientAndProfileDetails / fetchMyReviews intentionally omitted —
    // they're recreated when `referral` ref changes and we only want to run
    // the heavy reset on a new referralId.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, referralId]);

  // Keep status in sync if the parent mutates referral.status while the
  // modal is open (e.g. after fetchReferrals re-syncs selectedReferral).
  useEffect(() => {
    if (isOpen && referral) {
      setStatus(referral.status || "");
      setOriginalStatus(referral.status || "");
    }
  }, [isOpen, referral?.status, referral]);

  if (!isOpen || !referral) return null;

  const isStatusChanged = status !== originalStatus;

  // =========================
  // STATUS UPDATE
  // =========================
  const handleStatusChange = (newStatus) => {
    setStatus(newStatus);
  };

  const handleUpdateStatus = async () => {
    try {
      setIsSavingStatus(true);

      setGlobalLoadingMessage("Updating referral status...");
      setGlobalLoading(true);

      await apiFetch(`/referrals/bureau/${referral._id}`, {
        method: "PUT",
        body: JSON.stringify({ status }),
      });

      setOriginalStatus(status);

      onUpdate();
    } catch (err) {
      console.error("Failed to update status:", err);
      setError("Failed to update status");
    } finally {
      setIsSavingStatus(false);

      setGlobalLoading(false);
      setGlobalLoadingMessage("");
    }
  };

  // =========================
  // COMMENT EDIT
  // =========================
  const handleEditComment = () => {
    setIsEditing(true);

    setEditComment(referral.bureauNotes || "");
    setEditRating(referral.bureauRating?.toString() || "");
  };

  const handleSaveComment = async () => {
    try {
      setIsSavingComment(true);

      setGlobalLoadingMessage("Saving comment...");
      setGlobalLoading(true);

      await apiFetch(`/referrals/bureau/${referral._id}`, {
        method: "PUT",
        body: JSON.stringify({
          bureauNotes: editComment,
          bureauRating: editRating
            ? parseInt(editRating)
            : null,
        }),
      });

      setIsEditing(false);

      onUpdate();
    } catch (err) {
      console.error("Failed to save comment:", err);
      setError("Failed to save comment");
    } finally {
      setIsSavingComment(false);

      setGlobalLoading(false);
      setGlobalLoadingMessage("");
    }
  };

  const handleDeleteComment = async () => {
    try {
      setGlobalLoadingMessage("Deleting comment...");
      setGlobalLoading(true);

      await apiFetch(`/referrals/bureau/${referral._id}`, {
        method: "PUT",
        body: JSON.stringify({
          bureauNotes: null,
          bureauRating: null,
        }),
      });

      onUpdate();
    } catch (err) {
      console.error("Failed to delete comment:", err);
      setError("Failed to delete comment");
    } finally {
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className={`w-full ${
          isClient ? "max-w-2xl" : "max-w-4xl"
        } max-h-[90vh] bg-[var(--color-surface)] rounded-xl shadow-xl flex flex-col overflow-hidden`}
      >
        
        {/* HEADER */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-[var(--color-border)] flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-[var(--color-text-strong)]">
              Referral Details
            </h2>

            <p className="text-sm text-[var(--color-text-muted)]">
              {referral.profile?.fullName || "N/A"} •{" "}
              {referral.profile?.contactNumber || "N/A"}
            </p>
          </div>

          <div className="flex items-center gap-3">
            {isClient && (() => {
              const triggerStyle =
                statusStyleMap[status] || { bg: "#f3f4f6", fg: "#1f2937" };
              const arrow = encodeURIComponent(triggerStyle.fg);
              return (
                <select
                  value={
                    clientStatusOptions.some((o) => o.value === status)
                      ? status
                      : ""
                  }
                  disabled={isSavingStatus}
                  onChange={(e) => handleClientStatusChange(e.target.value)}
                  className="appearance-none px-3 py-1 pr-7 text-xs font-medium rounded-full border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[var(--color-primary)] disabled:opacity-60"
                  style={{
                    backgroundColor: triggerStyle.bg,
                    color: triggerStyle.fg,
                    backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 20 20' fill='${arrow}'%3E%3Cpath d='M5.25 7.5L10 12.25 14.75 7.5z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 0.5rem center",
                    backgroundSize: "0.65rem",
                  }}
                >
                  <option
                    value=""
                    disabled
                    style={{ backgroundColor: "#ffffff", color: "#6b7280" }}
                  >
                    Select Status
                  </option>
                  {clientStatusOptions.map((opt) => {
                    const s =
                      statusStyleMap[opt.value] || { bg: "#ffffff", fg: "#1f2937" };
                    return (
                      <option
                        key={opt.value}
                        value={opt.value}
                        style={{ backgroundColor: s.bg, color: s.fg }}
                      >
                        {opt.label}
                      </option>
                    );
                  })}
                </select>
              );
            })()}

            {!isClient && (
              <button
                onClick={fetchClientAndProfileDetails}
                disabled={loadingDetails}
                className="px-3 py-1 text-sm border border-[var(--color-border)] text-[var(--color-text)] rounded hover:bg-[var(--color-surface-hover)] disabled:opacity-50"
              >
                {loadingDetails ? "Refreshing..." : "Refresh"}
              </button>
            )}

            <button
              onClick={onClose}
              className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
            >
              ✕
            </button>
          </div>
        </div>

        {/* BODY */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">

          {/* LOADING */}
          {loadingDetails ? (
            <div className="text-center py-8">
              <div className="text-[var(--color-text-muted)]">
                Loading details...
              </div>
            </div>
          ) : (
            <>
              {/* PROFILE + CLIENT — single column for clients so the profile
                  stretches across the full modal width */}
              <div
                className={`grid gap-6 ${
                  isClient ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-2"
                }`}
              >

                {/* PROFILE — the matched candidate */}
                <div className="space-y-4">
                  <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-sm">
                    <ProfileFactsCard u={profileDetails || referral.profile || {}} />
                  </section>
                </div>

                {/* CLIENT — bureau-only (clients don't need to see their own info).
                    The client is a PublicUser post-merge, so the demographic
                    block is rendered with the SAME <ProfileFactsCard> the
                    profile uses on the left. The bureau-only extras (match
                    preferences, notes, activity, email) live below in the
                    same card. */}
                {!isClient && (() => {
                  const c = clientDetails || {};
                  // Top-level fields are the full PublicUser doc; the
                  // legacy `user` alias is a subset, so prefer top-level
                  // and only fall back to the alias for compatibility.
                  const range = c.preferredAgeRange || {};
                  const ageRange =
                    range.min || range.max
                      ? `${range.min ?? "—"} – ${range.max ?? "—"}`
                      : null;
                  const list = (arr) =>
                    Array.isArray(arr) && arr.length > 0 ? arr.join(", ") : null;
                  return (
                    <div className="space-y-4">
                      <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-sm">
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] mb-2">
                          Client
                        </div>
                        <ProfileFactsCard u={c} />

                        {c.email && (
                          <div className="mt-2 text-[12px] text-[var(--color-text)]">
                            Email: {c.email}
                          </div>
                        )}

                        <div className="font-medium text-sm mt-4">Match Preferences</div>
                        <ul className="list-disc ml-5 mt-1 text-xs">
                          <li>
                            Preferred Gender:{" "}
                            <span className="capitalize">
                              {c.preferredGender || "Any"}
                            </span>
                          </li>
                          <li>
                            Preferred Age Range: {ageRange || "Not specified"}
                          </li>
                          <li>
                            Preferred Cities:{" "}
                            {list(c.preferredCities) || "Not specified"}
                          </li>
                          <li>
                            Preferred Professions:{" "}
                            {list(c.preferredProfessions) || "Not specified"}
                          </li>
                          <li>
                            Preferred Castes:{" "}
                            {list(c.preferredCastes) || "Not specified"}
                          </li>
                        </ul>

                        {c.clientNotes && (
                          <>
                            <div className="font-medium text-sm mt-3">
                              Internal Notes
                            </div>
                            <div className="text-xs text-[var(--color-text)] whitespace-pre-wrap ml-1">
                              {c.clientNotes}
                            </div>
                          </>
                        )}

                        <div className="font-medium text-sm mt-3">Activity</div>
                        <div className="grid grid-cols-3 gap-2 text-sm mt-1">
                          <div>
                            <div className="text-[var(--color-text-muted)] text-xs">
                              Total Referrals
                            </div>
                            <div className="font-semibold">
                              {c.totalReferrals ?? 0}
                            </div>
                          </div>
                          <div>
                            <div className="text-[var(--color-text-muted)] text-xs">
                              Active
                            </div>
                            <div className="font-semibold">
                              {c.activeReferrals ?? 0}
                            </div>
                          </div>
                          <div>
                            <div className="text-[var(--color-text-muted)] text-xs">
                              Successful Matches
                            </div>
                            <div className="font-semibold">
                              {c.successfulMatches ?? 0}
                            </div>
                          </div>
                        </div>
                      </section>
                    </div>
                  );
                })()}
              </div>

              {/* USER FEEDBACK ON PROFILE — bureau-only.
                  All reviews left by public users on this profile, aggregated
                  across every referral. Lets the bureau see what feedback their
                  matched users have given. */}
              {!isClient && Array.isArray(profileDetails?.profileReviews) &&
                profileDetails.profileReviews.length > 0 && (
                <section>
                  <h3 className="text-sm font-semibold mb-2">
                    User Feedback on this Profile
                  </h3>
                  <div className="space-y-2">
                    {[...profileDetails.profileReviews]
                      .sort(
                        (a, b) =>
                          new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
                      )
                      .map((r) => (
                        <div
                          key={r._id}
                          className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-3 text-sm"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="text-xs text-[var(--color-text-muted)]">
                              <div className="font-medium text-[var(--color-text-strong)]">
                                {r.publicUserName || "Unknown user"}
                              </div>
                              {r.publicUserEmail && (
                                <div>{r.publicUserEmail}</div>
                              )}
                            </div>
                            <div className="text-xs text-[var(--color-text-muted)] whitespace-nowrap">
                              {r.rating ? `${r.rating}/5 • ` : ""}
                              {r.createdAt
                                ? new Date(r.createdAt).toLocaleString()
                                : ""}
                            </div>
                          </div>
                          <div className="mt-2 text-[var(--color-text)]">
                            {r.comment}
                          </div>
                        </div>
                      ))}
                  </div>
                </section>
              )}

              {/* PROFILE REVIEWS — list of past comments (form lives in sticky footer) */}
              {isClient && myReviews.length > 0 && (
                <section>
                  <h3 className="text-sm font-semibold mb-2">
                    My Past Comments
                  </h3>
                  <div className="space-y-2">
                    {myReviews.map((r) => {
                      const isEditingThis = editingReviewId === r._id;
                      return (
                        <div
                          key={r._id}
                          className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-3 text-sm"
                        >
                          {isEditingThis ? (
                            <div className="space-y-2">
                              <textarea
                                value={editingText}
                                onChange={(e) => setEditingText(e.target.value)}
                                rows={3}
                                className="w-full border rounded-lg px-3 py-2 text-sm"
                              />
                              <select
                                value={editingRating}
                                onChange={(e) => setEditingRating(e.target.value)}
                                className="w-full border rounded-lg px-3 py-2 text-sm"
                              >
                                <option value="">No rating</option>
                                <option value="1">1 - Poor</option>
                                <option value="2">2 - Below Average</option>
                                <option value="3">3 - Average</option>
                                <option value="4">4 - Good</option>
                                <option value="5">5 - Excellent</option>
                              </select>
                              <div className="flex gap-2 justify-end">
                                <button
                                  onClick={handleCancelEditReview}
                                  disabled={isSavingEdit}
                                  className={`${BTN_SECONDARY_SM} px-3 py-1`}
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={handleSaveEditReview}
                                  disabled={!editingText.trim() || isSavingEdit}
                                  className={`${BTN_PRIMARY_SM} px-3 py-1`}
                                >
                                  {isSavingEdit ? "Saving..." : "Save"}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="text-[var(--color-text)]">{r.comment}</div>
                              <div className="flex items-center justify-between mt-2 text-xs text-[var(--color-text-muted)]">
                                <span>
                                  {r.rating ? `Rating: ${r.rating}/5 • ` : ""}
                                  {r.createdAt
                                    ? new Date(r.createdAt).toLocaleString()
                                    : ""}
                                </span>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleStartEditReview(r)}
                                    className={`${BTN_PRIMARY_SM} px-2 py-1`}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleDeleteReview(r._id)}
                                    className={`${BTN_DANGER_SM} px-2 py-1`}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}

              {/* Bureau Update Status form lives in the sticky footer below
                  so it's always visible while scrolling the body. */}

              {/* Bureau "Your Comment" management lives in the sticky footer
                  below so it's always visible while scrolling the body. */}

              {/* ERROR */}
              {error && (
                <div className="text-sm text-red-500">
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        {/* FOOTER */}
        {isClient ? (
          <div className="px-6 py-4 border-t border-[var(--color-border)] bg-[var(--color-surface)] flex-shrink-0 space-y-2">
            <h3 className="text-sm font-semibold">
              My Comments on this Profile
            </h3>
            <textarea
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              rows={2}
              placeholder="Share your feedback about this profile..."
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
            <div className="flex items-center gap-2">
              <select
                value={reviewRating}
                onChange={(e) => setReviewRating(e.target.value)}
                className="flex-1 border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">No rating</option>
                <option value="1">1 - Poor</option>
                <option value="2">2 - Below Average</option>
                <option value="3">3 - Average</option>
                <option value="4">4 - Good</option>
                <option value="5">5 - Excellent</option>
              </select>
              <button
                onClick={handleSaveReview}
                disabled={!reviewText.trim() || isSavingReview}
                className={`${BTN_PRIMARY} px-4 py-2 text-sm`}
              >
                {isSavingReview ? "Saving..." : "Add Comment"}
              </button>
            </div>
          </div>
        ) : (
          <div className="px-6 py-4 border-t border-[var(--color-border)] bg-[var(--color-surface)] flex-shrink-0 space-y-3">
            <div>
              <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
                Update Status
              </label>
              <div className="flex gap-2">
                <select
                  value={status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  className="flex-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleUpdateStatus}
                  disabled={!isStatusChanged || isSavingStatus}
                  className={`${BTN_PRIMARY} px-4 py-2 text-sm`}
                >
                  {isSavingStatus ? "Updating..." : "Update"}
                </button>
              </div>
            </div>

            {/* Bureau comment management — pinned, always visible. */}
            <div>
              <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
                Your Comment
              </label>
              {isEditing ? (
                <div className="space-y-2">
                  <textarea
                    value={editComment}
                    onChange={(e) => setEditComment(e.target.value)}
                    rows={2}
                    placeholder="Write a note about this referral..."
                    className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
                  />
                  <div className="flex items-center gap-2">
                    <select
                      value={editRating}
                      onChange={(e) => setEditRating(e.target.value)}
                      className="flex-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
                    >
                      <option value="">No rating</option>
                      <option value="1">1 - Poor</option>
                      <option value="2">2 - Below Average</option>
                      <option value="3">3 - Average</option>
                      <option value="4">4 - Good</option>
                      <option value="5">5 - Excellent</option>
                    </select>
                    <button
                      onClick={handleSaveComment}
                      disabled={isSavingComment}
                      className={`${BTN_PRIMARY} px-3 py-2 text-sm`}
                    >
                      {isSavingComment ? "Saving..." : "Save"}
                    </button>
                    <button
                      onClick={() => setIsEditing(false)}
                      className={`${BTN_SECONDARY} px-3 py-2 text-sm`}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : referral.bureauNotes ? (
                <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-2 text-sm">
                  <div className="text-[var(--color-text)]">{referral.bureauNotes}</div>
                  {referral.bureauRating && (
                    <div className="text-xs text-[var(--color-text-muted)] mt-1">
                      Rating: {referral.bureauRating}/5
                    </div>
                  )}
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={handleEditComment}
                      className={`${BTN_PRIMARY_SM} px-2 py-1`}
                    >
                      Edit
                    </button>
                    <button
                      onClick={handleDeleteComment}
                      className={`${BTN_DANGER_SM} px-2 py-1`}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className={`${BTN_PRIMARY} px-4 py-2 text-sm`}
                >
                  Add Comment
                </button>
              )}
            </div>

            <div className="flex justify-end">
              <button
                onClick={onClose}
                className={`${BTN_SECONDARY} px-4 py-2 text-sm`}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferralDetailsModal;